import store from "@/store";
import {
  ref,
  reactive,
  onBeforeMount,
  onBeforeUnmount,
  getCurrentInstance,
  watch,
  onUpdated,
} from "@vue/composition-api";

import useDate from "@/utils/hooks/useDate";
import usePagination from "@/views/components/pagination/usePagination";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";
import useFlatPickr from "@/utils/hooks/useFlatPickr";
import useModal from "@/@core/components/base-modal/hooks/useModal";

import reportModel from "@/models/report/report";

import { defaultReportFilterType } from "@/configs/dynamic-config/dynamic-report";

export default function useHistoryReportModal(props) {
  const vm = getCurrentInstance().proxy;
  const { openModal, closeModal } = useModal();
  const {
    resolveDateWithSeperator,
    resolveDateWithComparator,
    resolveDate,
  } = useDate();

  const { pagination, onFilterEntity, onPageChange } = usePagination();

  const {
    openDefaultErrorModal,
    openWarningModal,
    openErrorModal,
  } = useValidationModal();

  const {
    fetchFileListModel,
    removeReportModel,
    fetchReportListModel,
    generateReportModel,
  } = reportModel();

  const loading = reactive({ tableData: false, btnReport: false });

  const historyReportList = ref([]);

  const keyChange = ref(0);

  const form = reactive({
    validFrom: { date: null, time: null },
    validThrough: { date: null, time: null },
    status: null,
  });

  const {
    validFrom,
    validThrough,
    flatpickrDateConfig,
    flatpickrTimeConfig,
    onOpenValidFromDatePicker,
    onOpenValidFromTimePicker,
    onOpenValidThroughTimePicker,
    onSelectValidFromDate,
    onSelectValidFromTimePicker,
    resolveDateString,
  } = useFlatPickr();

  const showModal = reactive({
    generate: false,
  });

  const onOpenGenerateModal = () => {
    showModal.generate = true;
    vm.$bvModal.show("generate-report-custom");
    openModal();
  };

  const closeGenerateModal = () => {
    vm.$bvModal.hide("generate-report-custom");
  };

  const intervalReportList = reactive({
    value: null,
    timer: 20000,
  });

  const fields = [
    { key: "report_seq", label: "No." },
    { key: "report_name", label: "Report" },
    { key: "filter", label: "Filter" },
    { key: "date_time", label: "Date Time" },
    { key: "updatedtime", label: "Last Update" },
    { key: "progress", label: "Progress" },
    { key: "status", label: "Status" },
    { key: "actions", label: "" },
  ];

  const statusOption = ref([]);

  const generateObject = reactive({
    reportKey: "",
    reportId: "",
    reportName: "",
    reportCategory: "",
  });

  const getReportByDyfaultModule = ({
    moduleTypeKey = null,
    moduleKey = null,
  }) => {
    loading.tableData = true;
    loading.btnReport = true;
    let promise = new Promise((resolve) => {
      fetchReportListModel.payload.Filters = [
        {
          Seq: 1,
          Key: moduleTypeKey,
          Value: moduleKey,
        },
      ];
      store
        .dispatch("api/apiRequest", fetchReportListModel)
        .then((response) => {
          let respData = response.data.resp;
          resolve(respData);
        })
        .catch((err) => {
          openDefaultErrorModal(err);
        })
        .finally(() => {
          loading.btnReport = false;
          loading.tableData = false;
        });
    });

    promise.then((respData) => {
      statusOption.value = [];
      if (moduleTypeKey == defaultReportFilterType.module) {
        let objReport = respData.find((o) => {
          return o.report_key == props.moduleKey;
        });
        if (objReport) {
          generateObject.reportId = objReport.report_id;
          generateObject.reportKey = objReport.report_key;
          generateObject.reportName = objReport.report_name;

          statusOption.value.push({
            label: objReport.report_name,
            value: 0,
            obj: { ...objReport },
          });

          if (objReport.report_category) {
            return getReportByDyfaultModule({
              moduleTypeKey: defaultReportFilterType.category,
              moduleKey: objReport.report_category,
            });
          }
        }
      } else {
        if (respData && respData.length > 0) {
          generateObject.reportId = respData[0].report_id;
          generateObject.reportKey = respData[0].report_key;
          generateObject.reportName = respData[0].report_name;

          respData.map((o, idx) => {
            statusOption.value.push({
              label: o.report_name,
              value: idx,
              obj: { ...o },
            });
          });
        }
        form.status = 0;
      }
    });
  };

  const fetchHistoryReport = (loadingEffect = true) => {
    if (loadingEffect) {
      loading.tableData = true;
    }

    const filterReport = {
      Seq: 1,
      Key: defaultReportFilterType.module,
      Value: props.moduleKey,
    };

    const filterReportGroupId = {
      Seq: 2,
      Key: defaultReportFilterType.filter,
      Value: props.GroupId || props.SurveyGroupId,
    };

    if (form.status !== null && form.status !== undefined) {
      const objFilter = [...statusOption.value];
      filterReport.Value = objFilter[form.status].obj?.report_key;
    }

    fetchFileListModel.payload.page = pagination.currentPage;
    fetchFileListModel.payload.limit = pagination.perPage;
    fetchFileListModel.payload.Filters =
      props.GroupId || props.SurveyGroupId
        ? [filterReport, filterReportGroupId]
        : [filterReport];

    store
      .dispatch("api/apiRequest", fetchFileListModel)
      .then((response) => {
        historyReportList.value = response.data.report_history_list;

        pagination.currentPage = response.data.current_page;
        pagination.totalRows = response.data.filtered_record;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => (loading.tableData = false));
  };

  const generateDefaultReport = ({ filterObj }) => {
    window.$loading(true);
    generateReportModel.payload.Report_Id = generateObject.reportId;
    generateReportModel.payload.Report_Key = generateObject.reportKey;

    if (filterObj) {
      generateReportModel.payload.Filters = filterObj;

      if (props.GroupId) {
        generateReportModel.payload.Filters.Quiz_GroupId = props.GroupId;
      } else if (props.SurveyGroupId) {
        generateReportModel.payload.Filters.Survey_GroupId =
          props.SurveyGroupId;
      }
    }

    if (form.status !== null && form.status !== undefined) {
      const objFilter = [...statusOption.value];
      generateReportModel.payload.Report_Id =
        objFilter[form.status].obj?.report_id;
      generateReportModel.payload.Report_Key =
        objFilter[form.status].obj?.report_key;
    }

    store
      .dispatch("api/apiRequest", generateReportModel)
      .then((response) => {
        closeGenerateModal();
      })
      .catch((error) => {
        openErrorModal("ไม่พบ Config Report", "กรุณาติดต่อทีม BCRM platform");
      })
      .finally(() => {
        fetchHistoryReport();
        window.$loading(false);
      });
  };

  const deleteReport = (report) => {
    removeReportModel.payload.Report_Id = report.report_id;
    removeReportModel.payload.Report_Job_Id = report.report_job_id;
    keyChange.value++;

    report.loading = true;
    loading.tableData = true;

    store
      .dispatch("api/apiRequest", removeReportModel)
      .then(() => {
        if (pagination.totalRows - 1 == 0) {
          return (pagination.currentPage = 1);
        }
        return fetchHistoryReport(false);
      })
      .finally(() => {
        report.loading = false;
        keyChange.value++;
      });
  };

  const onClickDownload = (link) => {
    const linkRef = vm.$refs[`download-${link}`];

    if (linkRef) {
      linkRef.click();
    }
  };

  const onClickDelete = (fileDetail) => {
    openWarningModal(
      "ลบ Report",
      `ต้องการลบ ${
        fileDetail.report_name ? `${fileDetail.report_name}` : "report นี้"
      } หรือไม่`,
      () => deleteReport(fileDetail)
    );
  };

  const onSelectReportCategory = ({ status, isFetchList = true }) => {
    if (status !== null && status !== undefined) form.status = status;
    if (isFetchList) fetchHistoryReport();
  };

  onBeforeMount(() => {
    if (!props.moduleTypeKey && !props.moduleKey) return;

    getReportByDyfaultModule({
      moduleTypeKey: props.moduleTypeKey,
      moduleKey: props.moduleKey,
    });
    fetchHistoryReport();
    intervalReportList.value = setInterval(() => {
      fetchHistoryReport();
    }, intervalReportList.timer);
  });

  onBeforeUnmount(() => {
    clearInterval(intervalReportList.value);
  });

  watch(
    () => pagination,
    () => {
      if (!loading.tableData) {
        fetchHistoryReport();
      }
    },
    { deep: true }
  );

  return {
    fields,
    historyReportList,
    loading,
    pagination,
    keyChange,
    resolveDateWithSeperator,
    resolveDateWithComparator,
    resolveDate,
    onPageChange,
    onFilterEntity,
    onClickDownload,
    onClickDelete,
    generateDefaultReport,
    generateObject,
    form,
    statusOption,

    validFrom,
    validThrough,
    flatpickrDateConfig,
    flatpickrTimeConfig,
    onOpenValidFromDatePicker,
    onOpenValidFromTimePicker,
    onOpenValidThroughTimePicker,
    onSelectValidFromDate,
    onSelectValidFromTimePicker,
    resolveDateString,
    onOpenGenerateModal,
    closeGenerateModal,
    showModal,
    onSelectReportCategory,
  };
}
